import React, { useState } from "react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";
import { Button } from "./ui";

// Reusable segmented contact form.
// props: category (string), detailLabel (string|null), detailPlaceholder
const ContactForm = ({ category, detailLabel = null, detailPlaceholder = "", testidPrefix = "contact" }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    detail: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitContact({ category, ...form });
      setDone(true);
      toast.success("Thank you — your enquiry has been received.");
      setForm({ name: "", email: "", phone: "", company: "", detail: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy placeholder:text-steel/60 focus:outline-none focus:border-navy transition-colors";
  const labelCls = "block text-xs tracking-kicker uppercase text-steel mb-2";

  if (done) {
    return (
      <div
        className="border border-navy/10 bg-stone px-8 py-12 text-center"
        data-testid={`${testidPrefix}-success`}
      >
        <p className="text-navy font-medium mb-2">Enquiry received</p>
        <p className="text-sm text-steel mb-6">
          Thank you. Your enquiry has been received and the right member of our team will be in
          touch shortly.
        </p>
        <Button variant="outline" onClick={() => setDone(false)} testid={`${testidPrefix}-reset`}>
          Submit another enquiry
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid={`${testidPrefix}-form`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Full name</label>
          <input
            className={inputCls}
            value={form.name}
            onChange={update("name")}
            required
            placeholder="[Your name]"
            data-testid={`${testidPrefix}-name`}
          />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input
            type="email"
            className={inputCls}
            value={form.email}
            onChange={update("email")}
            required
            placeholder="[you@example.com]"
            data-testid={`${testidPrefix}-email`}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Phone</label>
          <input
            className={inputCls}
            value={form.phone}
            onChange={update("phone")}
            placeholder="[Phone number]"
            data-testid={`${testidPrefix}-phone`}
          />
        </div>
        <div>
          <label className={labelCls}>Company</label>
          <input
            className={inputCls}
            value={form.company}
            onChange={update("company")}
            placeholder="[Company name]"
            data-testid={`${testidPrefix}-company`}
          />
        </div>
      </div>

      {detailLabel && (
        <div>
          <label className={labelCls}>{detailLabel}</label>
          <input
            className={inputCls}
            value={form.detail}
            onChange={update("detail")}
            placeholder={detailPlaceholder}
            data-testid={`${testidPrefix}-detail`}
          />
        </div>
      )}

      <div>
        <label className={labelCls}>Message</label>
        <textarea
          rows={5}
          className={inputCls}
          value={form.message}
          onChange={update("message")}
          placeholder="[How can we help?]"
          data-testid={`${testidPrefix}-message`}
        />
      </div>

      <Button
        type="submit"
        variant="primary"
        disabled={submitting}
        testid={`${testidPrefix}-submit`}
        className="w-full sm:w-auto"
      >
        {submitting ? "Sending…" : "Submit enquiry"}
      </Button>
    </form>
  );
};

export default ContactForm;
